<template>
  <div>
    <div class="analytics-cards mb-10">
      <AnalyticsCard
        v-for="card in cards"
        :key="card.id"
        :title="card.title"
        :icon="card.icon"
        :icon-color="card.iconColor"
        :value="card.value"
        :content-text="card.contentText"
        :footer="card.footer"
      />
    </div>
    <h3 class="mb-2 text-xl font-bold">
      {{ $t("label.managersAnalytics") }}
    </h3>
    <p class="mb-5 text-sm text-gray-500 md:hidden">
      {{ $t("label.managersAnalyticsDescriptionMobile") }}
    </p>
    <p class="mb-5 hidden text-sm text-gray-500 md:block">
      {{ $t("label.managersAnalyticsDescription") }}
    </p>

    <OurDropdownButton
      v-model="sortType"
      class="sorting-button mb-5 w-full md:hidden"
      :text="sortingText"
      :options="sortOptions"
      item-label="name"
      variant="thirdary"
      filled
    />

    <ManagerAnalyticsList
      :items="perManagerAnalytics"
      :totals="totalAnalytics"
      :order-by="orderBy"
      @sort="onSort"
    />
  </div>
</template>

<script>
import { mapState, mapGetters } from "vuex";
import AnalyticsCard from "../_components/AnalyticsCard";
import {
  ANALYTICS_CARDS_CONFIG,
  CARD_FORMATTERS,
  DEFAULT_MANAGER_ANALYTICS_SORT_OPTION_ID,
  MANAGER_ANALYTICS_SORT_OPTIONS,
} from "../config/analyticsCards.config";
import apolloMixin from "@/mixins/apollo.mixin";
import { dealershipApplicationsAnalyticsQuery } from "../gql/queries";
import DateService from "@/services/date.service";
import ManagerAnalyticsList from "../_components/ManagerAnalyticsList.vue";

const DEFAULT_SORTING = { column: "received", direction: "desc" };

const SORTABLE_COLUMNS = [
  "managerName",
  "received",
  "rejected",
  "inProgress",
  "dealerCreated",
  "madeOrder",
  "ordersSum",
  "shippedSum",
  "noOrders",
];

const parseSortingFromQuery = (sortParam) => {
  if (!sortParam) {
    return DEFAULT_SORTING;
  }

  const direction = sortParam.startsWith("-") ? "desc" : "asc";
  const column = direction === "desc" ? sortParam.slice(1) : sortParam;

  if (!SORTABLE_COLUMNS.includes(column)) {
    return DEFAULT_SORTING;
  }

  return { column, direction };
};

const sortingToQuery = ({ column, direction }) => {
  return direction === "desc" ? `-${column}` : column;
};

export default {
  name: "AnalyticsTab",

  components: { AnalyticsCard, ManagerAnalyticsList },

  mixins: [apolloMixin],

  data() {
    return {
      analyticsSummary: null,
      perManagerAnalytics: [],
      totalAnalytics: null,
      orderBy: DEFAULT_SORTING,
      sortType: DEFAULT_MANAGER_ANALYTICS_SORT_OPTION_ID,
    };
  },

  computed: {
    ...mapState("dealershipApplications", ["analyticsFiltersForm"]),
    ...mapGetters("dealershipApplications", ["getFilters"]),

    cards() {
      return ANALYTICS_CARDS_CONFIG.map((card) => this.mapCardConfig(card));
    },

    sortOptions() {
      return MANAGER_ANALYTICS_SORT_OPTIONS.map((option) => ({
        id: option.id,
        name: this.$t(option.labelKey),
        sortable: option.sortable,
      }));
    },

    sortingText() {
      return (
        this.sortOptions.find((option) => option.id === this.sortType)?.name ||
        this.$t("button.sorting")
      );
    },
  },

  watch: {
    analyticsFiltersForm: {
      handler: "onChangeFiltersForm",
      deep: true,
    },

    "$route.query.sort"(sort) {
      if (sortingToQuery(this.orderBy) === sort) {
        return;
      }

      this.orderBy = parseSortingFromQuery(sort);
      this.syncSortTypeWithOrderBy();
      this.getData();
    },

    sortType(value) {
      this.onChangeSortType(value);
    },
  },

  async created() {
    this.orderBy = parseSortingFromQuery(this.$route.query.sort);
    this.syncSortTypeWithOrderBy();
    await this.syncSortToUrl();
    this.getData();
  },

  methods: {
    syncSortToUrl() {
      const sort = sortingToQuery(this.orderBy);

      if (sort === this.$route.query.sort) {
        return Promise.resolve();
      }

      return this.$router.replace({
        name: this.$route.name,
        query: {
          ...this.$route.query,
          sort,
        },
      });
    },

    syncSortTypeWithOrderBy() {
      const sort = sortingToQuery(this.orderBy);
      const option = this.sortOptions.find((item) => item.sortable === sort);

      this.sortType = option?.id ?? DEFAULT_MANAGER_ANALYTICS_SORT_OPTION_ID;
    },

    onChangeSortType(sortTypeId) {
      const option = this.sortOptions.find((item) => item.id === sortTypeId);

      if (!option) {
        return;
      }

      const nextOrderBy = parseSortingFromQuery(option.sortable);

      if (
        nextOrderBy.column === this.orderBy.column &&
        nextOrderBy.direction === this.orderBy.direction
      ) {
        return;
      }

      this.orderBy = nextOrderBy;
      this.syncSortToUrl();
      this.getData();
    },

    async getData() {
      const rawFilters = this.getFilters(false) || {};
      const { date } = rawFilters;

      let createdAtFrom = null;
      let createdAtTo = null;

      if (date?.from) {
        createdAtFrom = DateService.dateRangeFilterConverter(date.from, "from");
      }

      if (date?.to) {
        createdAtTo = DateService.dateRangeFilterConverter(date.to, "to");
      }

      const response = await this.$get(dealershipApplicationsAnalyticsQuery, {
        filters: {
          managerIds: this.analyticsFiltersForm.managerIds,
          createdAtFrom,
          createdAtTo,
        },
        orderBy: this.orderBy,
      });

      this.analyticsSummary = response.dealershipApplicationsAnalytics?.summary || null;
      this.totalAnalytics = response.dealershipApplicationsAnalytics?.totals || null;
      this.perManagerAnalytics =
        response.dealershipApplicationsAnalytics?.perManager?.map(
          ({ managerName, ordersSumSharePercent, metrics }) => ({
            managerName,
            ...metrics,
            ordersSum: {
              ...(metrics.ordersSum || {}),
              sharePercent: ordersSumSharePercent,
            },
          })
        ) || [];
    },

    onChangeFiltersForm() {
      this.getData();
    },

    onSort(header) {
      const column = header.sortable;

      if (!column || !SORTABLE_COLUMNS.includes(column)) {
        return;
      }

      let direction = "desc";

      if (this.orderBy.column === column) {
        direction = this.orderBy.direction === "asc" ? "desc" : "asc";
      }

      this.orderBy = { column, direction };
      this.syncSortTypeWithOrderBy();
      this.syncSortToUrl();
      this.getData();
    },

    mapCardConfig(card) {
      const formatter = CARD_FORMATTERS[card.id];
      const formatted = this.analyticsSummary && formatter ? formatter(this.analyticsSummary) : {};
      const contentTextParams = formatted.contentTextParams ?? card.contentTextParams;

      const contentText = contentTextParams
        ? this.$t(card.contentTextKey, contentTextParams)
        : this.$t(card.contentTextKey);

      const footer = card.footer
        ? {
            left: {
              label: this.$t(card.footer.left.labelKey),
              value: formatted.footer?.left?.value ?? card.footer.left.value,
            },
            right: {
              label: this.$t(card.footer.right.labelKey),
              value: formatted.footer?.right?.value ?? card.footer.right.value,
            },
          }
        : null;

      return {
        id: card.id,
        title: this.$t(card.titleKey),
        icon: card.icon,
        iconColor: card.iconColor,
        value: formatted.value ?? card.value,
        contentText,
        footer,
      };
    },
  },
};
</script>

<style lang="postcss" scoped>
.analytics-cards {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  grid-gap: 10px;
}
</style>
